import { client } from "./client";
import type {
  JobStatus,
  MigrationJob,
  MigrationJobDetail,
  MigrationItem,
  MigrationPreviewResponse,
  MigrationSelection,
} from "../type";

export interface ListResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const BASE = "/search/admin/migrations";

export interface ListParams {
  page?: number;
  status?: JobStatus | "";
}

export const listJobs = async (
  params: ListParams
): Promise<ListResponse<MigrationJob>> => {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.status) qs.set("status", params.status);
  const resp = await client.get<ListResponse<MigrationJob>>(
    `${BASE}/${qs.toString() ? `?${qs}` : ""}`
  );
  return resp.data;
};

export const getJob = async (id: number): Promise<MigrationJobDetail> => {
  const resp = await client.get<MigrationJobDetail>(`${BASE}/${id}/`);
  return resp.data;
};

export const listItems = async (
  id: number,
  params: { page?: number; result?: "inserted" | "skipped" | "failed" | "" } = {}
): Promise<ListResponse<MigrationItem>> => {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.result) qs.set("result", params.result);
  const resp = await client.get<ListResponse<MigrationItem>>(
    `${BASE}/${id}/items/${qs.toString() ? `?${qs}` : ""}`
  );
  return resp.data;
};

export interface CreateJobPayload {
  selected_pks?: number[];
  selection?: MigrationSelection;
  scheduled_at?: string | null;
  conflict_resolution?: "skip" | "merge";
}

export const createJob = async (
  payload: CreateJobPayload
): Promise<MigrationJobDetail> => {
  const resp = await client.post<MigrationJobDetail>(`${BASE}/`, payload);
  return resp.data;
};

export const previewJob = async (
  selection: MigrationSelection
): Promise<MigrationPreviewResponse> => {
  const resp = await client.post<MigrationPreviewResponse>(
    `${BASE}/preview/`,
    { selection }
  );
  return resp.data;
};

export const cancelJob = async (id: number): Promise<void> => {
  await client.delete(`${BASE}/${id}/`);
};

export const deleteJob = cancelJob;

export const retryJob = async (id: number): Promise<MigrationJobDetail> => {
  const resp = await client.post<MigrationJobDetail>(`${BASE}/${id}/retry/`);
  return resp.data;
};

/**
 * Check which of the supplied beta indexes already exist on product.
 * Used by the browser panel to badge "already shipped" rows so admins
 * don't waste cart space on redundant picks.
 *
 * Server caps at 2000 indexes per call; the caller is expected to chunk
 * larger requests (one page of results at a time is well under the cap).
 */
export const checkInProduct = async (
  indexes: number[]
): Promise<number[]> => {
  if (indexes.length === 0) return [];
  const resp = await client.post<{ in_product: number[] }>(
    `${BASE}/check-in-product/`,
    { indexes }
  );
  return resp.data.in_product;
};

export const rollbackJob = async (jobId: number): Promise<void> => {
  await client.post(`${BASE}/${jobId}/rollback/`);
};
